import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Select, Space, Switch, Spin } from 'antd';
import { message } from 'antd';
import { customInstance } from '@/shared/api/mutator/custom-instance';
import { HrConfig } from '../../types';

const { Option } = Select;

const HrConfigView: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    customInstance<HrConfig>({ url: '/api/hr/config', method: 'GET' })
      .then((data) => form.setFieldsValue(data))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await customInstance({ url: '/api/hr/config', method: 'PUT', data: values });
      message.success('HR configuration saved');
    } catch {
      message.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin className="p-6" />;

  return (
    <div className="p-6">
      <Card
        title="HR Configuration"
        extra={<Button type="primary" onClick={handleSave} loading={saving}>Save</Button>}
        style={{ maxWidth: 700 }}
      >
        <Form form={form} layout="vertical">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div className="flex gap-4">
              <Form.Item name="workingDaysPerMonth" label="Working Days/Month" style={{ flex: 1 }}>
                <InputNumber min={1} max={31} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="workingHoursPerDay" label="Working Hours/Day" style={{ flex: 1 }}>
                <InputNumber min={1} max={24} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <div className="flex gap-4">
              <Form.Item name="currency" label="Currency" style={{ flex: 1 }}>
                <Input placeholder="e.g. TZS" maxLength={10} />
              </Form.Item>
              <Form.Item name="defaultBranch" label="Default Branch" style={{ flex: 1 }}>
                <Input placeholder="e.g. HQ" maxLength={10} />
              </Form.Item>
              <Form.Item name="fiscalYearStartMonth" label="Fiscal Year Start Month" style={{ flex: 1 }}>
                <Select>
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                    <Option key={i + 1} value={i + 1}>{m}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div className="flex gap-4">
              <Form.Item name="defaultPayrollFrequency" label="Default Payroll Frequency" style={{ flex: 1 }}>
                <Select>
                  <Option value="MONTHLY">Monthly</Option>
                  <Option value="BI_WEEKLY">Bi-Weekly</Option>
                  <Option value="WEEKLY">Weekly</Option>
                </Select>
              </Form.Item>
            </div>
            <div className="flex gap-4 items-center">
              <Form.Item name="payeEnabled" label="PAYE Enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="nhifEnabled" label="NHIF Enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
            <div className="flex gap-4">
              <Form.Item name="nssfEmployeeRate" label="NSSF Employee Rate" style={{ flex: 1 }}>
                <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="nssfEmployerRate" label="NSSF Employer Rate" style={{ flex: 1 }}>
                <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default HrConfigView;
